import type {
  MarkConfig,
  TrackEventData,
  IdentifyTraits,
  ConsentStatus,
  StorageData,
} from '../types';
import { MarkCore } from '../core/MarkCore';
import { HttpTransport } from '../core/HttpTransport';
import { BrowserStorage } from './BrowserStorage';
import { extractAttributionFromSearch } from './urlAttribution';
import { getPageviewDedupeKey, normalizePathnameForPageview } from './pageviewDedupeKey';
import { installPageLifecycle, onAfterPageChange, teardownPageLifecycle } from './pageLifecycle';
import { installPageScrollDepth } from './pageScrollDepth';
import { installPageEngagement, resolvePageEngagementOptions } from './pageEngagement';

export class Mark {
  private static client: MarkCore | null = null;
  private static storage: BrowserStorage | null = null;
  private static config: MarkConfig | null = null;
  private static pageviewTrackerInstalled = false;
  private static lastPageviewDedupeKey: string | null = null;
  private static emitAutoPageview: (() => void) | null = null;
  private static pendingAttribution: Partial<StorageData> = {};
  private static beaconDrainHandler: (() => void) | null = null;
  private static visibilityHandler: (() => void) | null = null;
  private static onlineHandler: (() => void) | null = null;
  private static kickTimer: ReturnType<typeof setInterval> | null = null;

  public static init(config: MarkConfig) {
    if (!Mark.client) {
      const storage = new BrowserStorage({
        ...config.cross_domain,
        seed_visitor_id: config.visitor_id,
      });
      const transport = new HttpTransport(config);
      Mark.client = new MarkCore(config, { storage, transport });
      Mark.storage = storage;
      Mark.config = config;
      Mark.refreshAttribution(storage, config);
      if (typeof window !== 'undefined') {
        Mark.installPageLifecycleIfNeeded(config);
        if (config.autocapture?.pageview) {
          Mark.installPageviewTracking(config, storage);
        }
        Mark.installExtendedAutocapture(config);
        Mark.installLifecycleFlushing();
      }
    }
    return Mark.client;
  }

  private static installLifecycleFlushing() {
    if (typeof window === 'undefined') return;
    if (Mark.beaconDrainHandler) return;

    const drain = () => {
      Mark.client?.drainViaBeacon();
    };
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        drain();
      }
    };
    const onOnline = () => {
      Mark.client?.kickQueue();
    };

    Mark.beaconDrainHandler = drain;
    Mark.visibilityHandler = onVisibility;
    Mark.onlineHandler = onOnline;

    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('pagehide', drain);
      window.addEventListener('beforeunload', drain);
      window.addEventListener('online', onOnline);
    }

    if (typeof setInterval === 'function') {
      Mark.kickTimer = setInterval(() => {
        Mark.client?.kickQueue();
      }, 30000);
    }
  }

  public static track(eventName: string, data: TrackEventData = {}) {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return;
    }
    Mark.client.track(eventName, data);
  }

  public static identify(userId: string, traits: IdentifyTraits = {}) {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return;
    }
    Mark.client.identify(userId, traits);
  }

  public static conversion(eventName: string, data: TrackEventData = {}) {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return;
    }
    Mark.client.conversion(eventName, data);
  }

  public static setConsent(status: ConsentStatus) {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return;
    }
    Mark.client.setConsent(status);
    if (status === 'granted') {
      Mark.flushPendingAttribution();
      // When auto pageview is enabled, attempt a page_view immediately after consent grant.
      Mark.emitAutoPageview?.();
    }
  }

  /**
   * Returns the current visitor ID when available.
   * When consent is required (`require_consent: true` or `'auto'`), returns `undefined` until consent is granted.
   * Use this to pass the visitor ID to your backend (e.g. in a header or request body) for server-side attribution
   * when you do not have an authenticated user ID.
   */
  public static getVisitorId(): string | undefined {
    if (!Mark.client || !Mark.storage || !Mark.config) {
      return undefined;
    }
    const requirement = Mark.config.require_consent ?? false;
    if (requirement && Mark.storage.getConsentStatus() !== 'granted') {
      return undefined;
    }
    return Mark.client.getVisitorId();
  }

  /**
   * Adopts or replaces the stored visitor id with a trusted first-party value.
   * Updates in-memory storage, localStorage, and the visitor cookie immediately.
   * Use when an external anonymous id (for example a platform client id) is
   * available after init. Theme embeds and pixels should call this with the
   * same source id. Returns false when the id is invalid or the SDK is not
   * initialized.
   */
  public static setVisitorId(visitorId: string): boolean {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return false;
    }
    return Mark.client.setVisitorId(visitorId);
  }

  public static flush(): Promise<void> {
    if (!Mark.client) {
      return Promise.resolve();
    }
    return Mark.client.flush();
  }

  /**
   * Flags (or un-flags) the current visitor as internal traffic. Persists
   * across reloads via the SDK's browser storage. Cleared by `reset()` and by
   * `setConsent('denied')`.
   *
   * The policy for deciding *when* to call this (URL query parameter, auth
   * role, IP, feature flag, etc.) is intentionally left to the host app.
   *
   * @example
   *   // URL-based opt-in: call once during app bootstrap, after Mark.init()
   *   const params = new URLSearchParams(window.location.search);
   *   if (params.get('onelence_internal') === '1') Mark.setInternal(true);
   *   if (params.get('onelence_internal') === '0') Mark.setInternal(false);
   */
  public static setInternal(value: boolean) {
    if (!Mark.client) {
      if (Mark.config?.debug) {
        console.warn('[Mark] Not initialized. Call init() first.');
      }
      return;
    }
    Mark.client.setInternal(value);
  }

  public static getInternal(): boolean {
    return Mark.client?.getInternal() ?? false;
  }

  public static reset() {
    Mark.client?.reset();
    Mark.pendingAttribution = {};
    Mark.lastPageviewDedupeKey = null;
  }

  public static getStats() {
    return Mark.client?.getStats() ?? { queued: 0, sent: 0, failed: 0, dropped: 0 };
  }

  public static destroy() {
    if (typeof window === 'undefined') return;
    teardownPageLifecycle();
    if (Mark.beaconDrainHandler) {
      window.removeEventListener('pagehide', Mark.beaconDrainHandler);
      window.removeEventListener('beforeunload', Mark.beaconDrainHandler);
    }
    if (Mark.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', Mark.visibilityHandler);
    }
    if (Mark.onlineHandler) {
      window.removeEventListener('online', Mark.onlineHandler);
    }
    if (Mark.kickTimer) {
      clearInterval(Mark.kickTimer);
    }
    Mark.beaconDrainHandler = null;
    Mark.visibilityHandler = null;
    Mark.onlineHandler = null;
    Mark.kickTimer = null;
    Mark.pageviewTrackerInstalled = false;
  }

  private static installPageLifecycleIfNeeded(config: MarkConfig) {
    const auto = config.autocapture;
    const needsLifecycle =
      Boolean(auto?.pageview) || Boolean(auto?.scroll_depth) || Boolean(auto?.page_engagement);
    if (!needsLifecycle) return;
    const trackRouteChanges = config.track_route_changes ?? true;
    installPageLifecycle({ trackRouteChanges });
  }

  private static installPageviewTracking(config: MarkConfig, storage: BrowserStorage) {
    if (Mark.pageviewTrackerInstalled) return;
    Mark.pageviewTrackerInstalled = true;

    const emit = () => {
      if (!Mark.client) return;
      Mark.refreshAttribution(storage, config);
      const dedupeKey = getPageviewDedupeKey(window.location);
      if (dedupeKey === Mark.lastPageviewDedupeKey) return;
      const sent = Mark.client.track('page_view', {
        site: window.location.host,
        page: normalizePathnameForPageview(window.location.pathname),
        title: document.title,
        referrer: document.referrer || undefined,
      });
      if (sent) {
        Mark.lastPageviewDedupeKey = dedupeKey;
      }
    };
    Mark.emitAutoPageview = emit;

    emit();

    const trackRoutes = config.track_route_changes ?? true;
    if (trackRoutes) {
      onAfterPageChange(() => {
        emit();
      });
    }
  }

  private static refreshAttribution(storage: BrowserStorage, config: MarkConfig) {
    if (typeof window === 'undefined') {
      return;
    }
    const updates = extractAttributionFromSearch(window.location.search, config);
    if (Object.keys(updates).length === 0) {
      return;
    }
    if (Mark.shouldPersistAttribution(storage, config)) {
      const merged = Mark.mergeAttributionUpdates(Mark.pendingAttribution, updates);
      storage.update(merged);
      Mark.pendingAttribution = {};
      return;
    }
    Mark.pendingAttribution = Mark.mergeAttributionUpdates(Mark.pendingAttribution, updates);
  }

  private static flushPendingAttribution() {
    const storage = Mark.storage;
    const config = Mark.config;
    if (!storage || !config || typeof window === 'undefined') {
      return;
    }
    const fromUrl = extractAttributionFromSearch(window.location.search, config);
    const merged = Mark.mergeAttributionUpdates(Mark.pendingAttribution, fromUrl);
    if (Object.keys(merged).length > 0) {
      storage.update(merged);
    }
    Mark.pendingAttribution = {};
  }

  private static shouldPersistAttribution(storage: BrowserStorage, config: MarkConfig) {
    const requirement = config.require_consent ?? false;
    if (!requirement) {
      return true;
    }
    return storage.getConsentStatus() === 'granted';
  }

  private static mergeAttributionUpdates(
    previous: Partial<StorageData>,
    next: Partial<StorageData>
  ): Partial<StorageData> {
    return {
      ...previous,
      ...next,
      query_params: {
        ...(previous.query_params ?? {}),
        ...(next.query_params ?? {}),
      },
    };
  }

  private static installExtendedAutocapture(config: MarkConfig) {
    if (!Mark.client || typeof document === 'undefined') return;
    const auto = config.autocapture;
    if (!auto) return;

    if (auto.click) {
      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        const selector = typeof auto.click === 'object' ? auto.click.selector : undefined;
        const element = selector ? target.closest(selector) : target.closest('[data-mark-event]');
        if (!element) return;
        const eventName = element.getAttribute('data-mark-event') || 'click';
        Mark.client?.track(eventName, {
          element_id: element.id || undefined,
          element_classes: element.className || undefined,
          text: element.textContent?.trim().slice(0, 120) || undefined,
          href: (element as HTMLAnchorElement).href || undefined,
        });
      });
    }

    if (auto.form_submit) {
      document.addEventListener('submit', (event) => {
        const form = event.target as HTMLFormElement | null;
        if (!form) return;
        Mark.client?.track('form_submit', {
          form_id: form.id || undefined,
          form_name: form.name || undefined,
          action: form.action || undefined,
        });
      });
    }

    if (auto.outbound_link) {
      document.addEventListener('click', (event) => {
        const anchor = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
        if (!anchor || !anchor.href) return;
        try {
          const targetUrl = new URL(anchor.href, window.location.href);
          const current = new URL(window.location.href);
          if (targetUrl.origin !== current.origin) {
            Mark.client?.trackWithOptions('outbound_link_click', { href: targetUrl.toString() }, { preferBeacon: true });
          }
        } catch {
          // ignore malformed URLs
        }
      });
    }

    if (auto.scroll_depth && Mark.client) {
      installPageScrollDepth(Mark.client);
    }

    if (auto.page_engagement && Mark.client) {
      installPageEngagement(Mark.client, resolvePageEngagementOptions(auto.page_engagement));
    }

    if (auto.web_vitals) {
      void import('web-vitals')
        .then((mod) => {
          const emit = (metric: { name: string; value: number; rating?: string; id?: string }) => {
            Mark.client?.track('web_vital', {
              metric: metric.name,
              value: metric.value,
              rating: metric.rating,
              metric_id: metric.id,
            });
          };
          mod.onLCP(emit);
          mod.onCLS(emit);
          mod.onINP(emit);
          mod.onTTFB(emit);
        })
        .catch(() => {
          if (config.debug) {
            console.warn('[Mark] web-vitals package not available; skipping autocapture.web_vitals.');
          }
        });
    }
  }
}

